"use client";

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Shield, Bug, AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Lock, Send, Zap, FileText, ExternalLink, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import Link from 'next/link';
import { motion, AnimatePresence } from 'motion/react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { 
      duration: 0.5, 
      ease: "easeOut" as const
    } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const popIn = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: { 
    scale: 1, 
    opacity: 1, 
    transition: { 
      type: "spring" as const, 
      stiffness: 500, 
      damping: 25 
    } 
  }
};

// Modified the formSchema to make agreeToTerms a boolean instead of a literal true
const formSchema = z.object({
  name: z.string().min(2, { message: 'Name must be at least 2 characters.' }),
  email: z.string().email({ message: 'Please enter a valid email address.' }),
  vulnerabilityType: z.string().min(1, { message: 'Please select a vulnerability type.' }),
  impactLevel: z.string().min(1, { message: 'Please select an impact level.' }),
  description: z.string().min(10, { message: 'Description must be at least 10 characters.' }),
  stepsToReproduce: z.string().min(10, { message: 'Steps to reproduce must be at least 10 characters.' }),
  possibleSolution: z.string().optional(),
  affectedUrls: z.string().optional(),
  attachments: z.any().optional(),
  agreeToTerms: z.boolean().refine(val => val === true, {
    message: 'You must agree to the terms.',
  }),
});

type FormValues = z.infer<typeof formSchema>;

export default function VulnerabilityPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFaq1, setShowFaq1] = useState(false);
  const [showFaq2, setShowFaq2] = useState(false);
  const [showFaq3, setShowFaq3] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: '',
      vulnerabilityType: '',
      impactLevel: '',
      description: '',
      stepsToReproduce: '',
      possibleSolution: '',
      affectedUrls: '',
      agreeToTerms: false, // This is fine now as we're using a refined boolean
    },
  });

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      console.log('Form submitted:', data);
      toast.success('Vulnerability report submitted successfully!', {
        description: 'Our security team will review your report and respond as soon as possible.',
      });
      form.reset();
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <main className="grow">
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="relative py-16 overflow-hidden"
        >
          {/* Animated background elements */}
          <div className="absolute inset-0 bg-linear-to-br from-blue-600 via-blue-500 to-blue-700 overflow-hidden">
            <motion.div 
              className="absolute top-0 left-0 right-0 h-1/2 w-full"
              style={{
                background: 'radial-gradient(circle at 50% 0%, rgba(255, 255, 255, 0.15) 0%, transparent 60%)'
              }}
              animate={{ 
                y: [0, 10, 0], 
                opacity: [0.7, 0.9, 0.7] 
              }}
              transition={{ 
                duration: 8, 
                repeat: Infinity,
                ease: "easeInOut" as const
              }}
            />
            
            {/* Floating particles */}
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={`particle-${i}`}
                className="absolute rounded-full bg-white/10"
                style={{
                  width: Math.random() * 12 + 4,
                  height: Math.random() * 12 + 4,
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, -50],
                  opacity: [0, 0.7, 0],
                  scale: [0, 1, 0.5]
                }}
                transition={{
                  duration: Math.random() * 5 + 7,
                  repeat: Infinity,
                  delay: Math.random() * 10,
                  ease: "easeInOut" as const
                }}
              />
            ))}
          </div>

          <div className="container mx-auto px-4 z-10 relative">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col md:flex-row items-center justify-center md:justify-start gap-6 text-center md:text-left">
                <motion.div
                  initial={{ opacity: 0, scale: 0.5, rotate: -10 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  transition={{ 
                    type: "spring" as const, 
                    stiffness: 100, 
                    damping: 15
                  }}
                >
                  <div className="bg-white/15 p-5 rounded-full backdrop-blur-sm shadow-lg border border-white/20 hover:bg-white/20 backdrop-blur-sm">
                    <Shield className="h-14 w-14 text-white" />
                    <motion.div 
                      animate={{ 
                        opacity: [0.3, 0.7, 0.3],
                        scale: [0.95, 1.05, 0.95],
                      }}
                      transition={{ 
                        duration: 3, 
                        repeat: Infinity,
                        ease: "easeInOut" as const
                      }}
                      className="absolute inset-0 rounded-full bg-white/10 blur-md -z-10"
                    />
                  </div>
                </motion.div>
                
                <div>
                  <motion.h1 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3"
                  >
                    Report a <span className="text-yellow-200">Security Vulnerability</span>
                  </motion.h1>
                  
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="text-lg text-blue-100 max-w-2xl"
                  >
                    We take security seriously. If you've discovered a vulnerability in our systems, 
                    we appreciate your help in disclosing it to us responsibly.
                  </motion.p>
                </div>
              </div>
              
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-8 flex flex-wrap justify-center md:justify-start gap-4"
              >
                <Link href="/bug-bounty">
                  <motion.div 
                    whileHover={{ scale: 1.05 }} 
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button variant="outline" className="bg-white/10 text-white border-white/20 hover:bg-white/20 backdrop-blur-sm">
                      <Bug className="h-4 w-4 mr-2" />
                      Bug Bounty Program
                    </Button>
                  </motion.div>
                </Link>
                
                <Link href="/famehall">
                  <motion.div 
                    whileHover={{ scale: 1.05 }} 
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button variant="secondary" className="bg-yellow-400 hover:bg-yellow-300 text-gray-900 border-yellow-300 shadow-lg font-semibold">
                      <Trophy className="h-4 w-4 mr-2" />
                      Hall of Fame
                    </Button>
                  </motion.div>
                </Link>
              </motion.div>
            </div>
          </div>
          
          {/* Wave divider */}
          <div className="absolute bottom-0 left-0 right-0 h-16 overflow-hidden">
            <svg
              preserveAspectRatio="none"
              className="h-full w-full"
              viewBox="0 0 1200 120"
              xmlns="http://www.w3.org/2000/svg"
              style={{ fill: '#ffffff' }}
            >
              <path
                d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
                opacity=".25"
              />
              <path
                d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
                opacity=".5"
              />
              <path
                d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
              />
            </svg>
          </div>
        </motion.div>
        
        <div className="container mx-auto px-4 mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <motion.div 
              className="lg:col-span-2"
              variants={fadeIn}
              initial="hidden"
              animate="visible"
            >
              <Card className="mb-8 border-0 shadow-lg overflow-hidden">
                <CardHeader className="bg-linear-to-r from-blue-50 to-white border-b border-blue-100/30">
                  <div className="flex items-center gap-3">
                    <motion.div 
                      whileHover={{ rotate: [0, -10, 20, -10, 0] }}
                      transition={{ duration: 0.6 }}
                      className="bg-blue-100 text-blue-700 p-2 rounded-full"
                    >
                      <Bug className="h-5 w-5" />
                    </motion.div>
                    <div>
                      <CardTitle className="text-xl text-blue-900">Report a Vulnerability</CardTitle>
                      <CardDescription className="text-blue-700/80">
                        Please provide detailed information about the vulnerability you've discovered.
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="pt-6">
                  <Form {...form}>
                    <motion.form 
                      onSubmit={form.handleSubmit(onSubmit)} 
                      className="space-y-6"
                      variants={staggerContainer}
                      initial="hidden"
                      animate="visible"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <motion.div variants={fadeIn}>
                          <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Your Name</FormLabel>
                                <FormControl>
                                  <Input placeholder="John Doe" className="border-gray-200 focus:ring-blue-500 transition-all" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </motion.div>
                        
                        <motion.div variants={fadeIn}>
                          <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Email Address</FormLabel>
                                <FormControl>
                                  <Input type="email" placeholder="your@email.com" className="border-gray-200 focus:ring-blue-500 transition-all" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </motion.div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <motion.div variants={fadeIn}>
                          <FormField
                            control={form.control}
                            name="vulnerabilityType"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Vulnerability Type</FormLabel>
                                <FormControl>
                                  <select
                                    className="flex h-10 w-full rounded-md border border-gray-200 bg-background px-3 py-2 text-base file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                                    {...field}
                                  >
                                    <option value="">Select vulnerability type</option>
                                    <option value="xss">Cross-Site Scripting (XSS)</option>
                                    <option value="sql-injection">SQL Injection</option>
                                    <option value="csrf">Cross-Site Request Forgery (CSRF)</option>
                                    <option value="authentication">Authentication Issues</option>
                                    <option value="data-exposure">Sensitive Data Exposure</option>
                                    <option value="access-control">Access Control Issue</option>
                                    <option value="configuration">Security Misconfiguration</option>
                                    <option value="other">Other</option>
                                  </select>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </motion.div>
                        
                        <motion.div variants={fadeIn}>
                          <FormField
                            control={form.control}
                            name="impactLevel"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Impact Level</FormLabel>
                                <FormControl>
                                  <select
                                    className="flex h-10 w-full rounded-md border border-gray-200 bg-background px-3 py-2 text-base file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                                    {...field}
                                  >
                                    <option value="">Select impact level</option>
                                    <option value="critical">Critical</option>
                                    <option value="high">High</option>
                                    <option value="medium">Medium</option>
                                    <option value="low">Low</option>
                                    <option value="info">Informational</option>
                                  </select>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </motion.div>
                      </div>
                      
                      <motion.div variants={fadeIn}>
                        <FormField
                          control={form.control}
                          name="description"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Vulnerability Description</FormLabel>
                              <FormControl>
                                <Textarea 
                                  placeholder="Please provide a detailed description of the vulnerability..."
                                  className="min-h-[120px] border-gray-200 focus:ring-blue-500 transition-all"
                                  {...field} 
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </motion.div>
                      
                      <motion.div variants={fadeIn}>
                        <FormField
                          control={form.control}
                          name="stepsToReproduce"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Steps to Reproduce</FormLabel>
                              <FormControl>
                                <Textarea 
                                  placeholder="1. Go to example.com/page\n2. Click on X\n3. Enter the following value: [value]\n4. Observe the result..."
                                  className="min-h-[120px] border-gray-200 focus:ring-blue-500 transition-all"
                                  {...field} 
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </motion.div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <motion.div variants={fadeIn}>
                          <FormField
                            control={form.control}
                            name="possibleSolution"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Possible Solution (Optional)</FormLabel>
                                <FormControl>
                                  <Textarea 
                                    placeholder="If you have suggestions for fixing the issue..."
                                    className="min-h-[80px] border-gray-200 focus:ring-blue-500 transition-all" 
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </motion.div>
                        
                        <motion.div variants={fadeIn}>
                          <FormField
                            control={form.control}
                            name="affectedUrls"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Affected URLs (Optional)</FormLabel>
                                <FormControl>
                                  <Textarea 
                                    placeholder="List any URLs where this vulnerability can be observed..."
                                    className="min-h-[80px] border-gray-200 focus:ring-blue-500 transition-all" 
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </motion.div>
                      </div>
                      
                      <motion.div variants={fadeIn}>
                        <FormField
                          control={form.control}
                          name="agreeToTerms"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-gray-200 p-4 shadow-sm">
                              <FormControl>
                                <input
                                  type="checkbox"
                                  id="agreeToTerms"
                                  className="h-4 w-4 mt-1 border-gray-300 rounded focus:ring-blue-500"
                                  checked={field.value}
                                  onChange={field.onChange}
                                />
                              </FormControl>
                              <div className="space-y-1 leading-none">
                                <FormLabel>
                                  I agree to RentWise's vulnerability disclosure policy
                                </FormLabel>
                                <FormDescription>
                                  I will not disclose this vulnerability publicly until RentWise has been given a reasonable amount of time to fix the issue.
                                </FormDescription>
                                <FormMessage />
                              </div>
                            </FormItem>
                          )}
                        />
                      </motion.div>
                      
                      <motion.div 
                        className="flex justify-end"
                        variants={fadeIn}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Button 
                          type="submit" 
                          disabled={isSubmitting}
                          className="bg-[var(--color-brand-primary)] hover:bg-[var(--color-brand-primary-hover)] text-white shadow-md"
                        >
                          {isSubmitting ? (
                            <div className="flex items-center gap-2">
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                              >
                                <svg className="h-4 w-4" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                              </motion.div>
                              <span>Submitting...</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Send className="h-4 w-4" />
                              <span>Submit Report</span>
                            </div>
                          )}
                        </Button>
                      </motion.div>
                    </motion.form>
                  </Form>
                </CardContent>
              </Card>
            </motion.div>
            
            <motion.div 
              className="lg:col-span-1"
              variants={fadeIn}
              initial="hidden"
              animate="visible"
              transition={{ delay: 0.3 }}
            >
              <motion.div
                variants={popIn}
                className="mb-6"
              >
                <Card className="border-0 shadow-lg overflow-hidden">
                  <CardHeader className="bg-linear-to-r from-blue-50 to-white border-b border-blue-100/30 pb-3">
                    <div className="flex items-center gap-3">
                      <motion.div
                        animate={{ rotate: [0, 10, 0, -10, 0] }}
                        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" as const }}
                        className="bg-blue-100 text-blue-700 p-2 rounded-full"
                      >
                        <Lock className="h-5 w-5" />
                      </motion.div>
                      <CardTitle className="text-lg text-blue-900">Our Security Policy</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <motion.ul className="space-y-4" variants={staggerContainer} initial="hidden" animate="visible">
                      {[
                        "We promise to acknowledge your report within 48 hours",
                        "We'll provide updates on the status of your report",
                        "We recognize security researchers in our acknowledgments page",
                        "We offer rewards for significant vulnerabilities through our bug bounty program"
                      ].map((item, i) => (
                        <motion.li 
                          key={i} 
                          className="flex items-start gap-2"
                          variants={fadeIn}
                          transition={{ delay: i * 0.1 }}
                          whileHover={{ x: 3 }}
                        >
                          <motion.div
                            whileHover={{ scale: 1.2, rotate: 10 }}
                            transition={{ type: "spring" as const, stiffness: 300 }}
                          >
                            <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
                          </motion.div>
                          <span className="text-sm text-gray-700">{item}</span>
                        </motion.li>
                      ))}
                    </motion.ul>
                  </CardContent>
                </Card>
              </motion.div>
              
              <motion.div variants={popIn} transition={{ delay: 0.2 }}>
                <Card className="border-0 shadow-lg overflow-hidden">
                  <CardHeader className="bg-linear-to-r from-blue-50 to-white border-b border-blue-100/30 pb-3">
                    <div className="flex items-center gap-3">
                      <motion.div 
                        whileHover={{ scale: 1.1, rotate: 10 }}
                        className="bg-blue-100 text-blue-700 p-2 rounded-full"
                      >
                        <FileText className="h-5 w-5" />
                      </motion.div>
                      <CardTitle className="text-lg text-blue-900">FAQ</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4 pt-4">
                    <div>
                      <button 
                        onClick={() => setShowFaq1(!showFaq1)}
                        className="flex justify-between items-center w-full text-left font-medium text-gray-900 rounded-lg p-2 hover:bg-blue-50 transition-colors"
                      >
                        <span>What should I include in my report?</span>
                        <motion.div 
                          animate={{ rotate: showFaq1 ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <ChevronDown className="h-5 w-5 text-blue-600" />
                        </motion.div>
                      </button>
                      
                      <AnimatePresence>
                        {showFaq1 && (
                          <motion.div 
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <div className="p-3 text-sm text-gray-600 bg-gray-50 rounded-lg mt-2">
                              <p>Please include a clear description of the issue, steps to reproduce, affected URLs, and if possible, screenshots or proof of concept. The more detailed your report, the faster we can address the vulnerability.</p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <button 
                        onClick={() => setShowFaq2(!showFaq2)}
                        className="flex justify-between items-center w-full text-left font-medium text-gray-900 rounded-lg p-2 hover:bg-blue-50 transition-colors"
                      >
                        <span>How long does it take to fix issues?</span>
                        <motion.div 
                          animate={{ rotate: showFaq2 ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <ChevronDown className="h-5 w-5 text-blue-600" />
                        </motion.div>
                      </button>
                      
                      <AnimatePresence>
                        {showFaq2 && (
                          <motion.div 
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <div className="p-3 text-sm text-gray-600 bg-gray-50 rounded-lg mt-2">
                              <p>Our response time depends on the severity of the issue. Critical vulnerabilities are addressed immediately, while lower priority issues may take more time. We strive to fix all valid security issues within 90 days of verification.</p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <button 
                        onClick={() => setShowFaq3(!showFaq3)}
                        className="flex justify-between items-center w-full text-left font-medium text-gray-900 rounded-lg p-2 hover:bg-blue-50 transition-colors"
                      >
                        <span>Do you offer bounties for vulnerabilities?</span>
                        <motion.div 
                          animate={{ rotate: showFaq3 ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <ChevronDown className="h-5 w-5 text-blue-600" />
                        </motion.div>
                      </button>
                      
                      <AnimatePresence>
                        {showFaq3 && (
                          <motion.div 
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <div className="p-3 text-sm text-gray-600 bg-gray-50 rounded-lg mt-2">
                              <p>Yes, we offer rewards for eligible security vulnerabilities. The reward amount depends on the severity and impact of the vulnerability. Please see our Bug Bounty Program page for more details on eligibility and reward amounts.</p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
              
              <motion.div 
                variants={fadeIn}
                transition={{ delay: 0.4 }}
                className="mt-6"
              >
                <Alert className="border-yellow-300 bg-yellow-100 shadow-sm border-0">
                  <AlertTriangle className="h-4 w-4 text-yellow-700" />
                  <AlertTitle className="text-yellow-900 flex items-center gap-2">
                    <motion.span
                      animate={{ x: [0, 2, 0, -2, 0] }}
                      transition={{ 
                        repeat: 3,
                        repeatDelay: 5,
                        duration: 0.5
                      }}
                    >
                      Important Note
                    </motion.span>
                  </AlertTitle>
                  <AlertDescription className="text-yellow-800">
                    Never attempt to access, modify, or delete data that does not belong to you. Testing should only be performed on your own accounts or test accounts provided by us.
                  </AlertDescription>
                </Alert>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
} 